import { FC, FormEvent, ReactNode } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { BRAND_DIALOG_CLASS } from '../../theme';
import { colors } from '../../theme/tokens';

// The XD modal for forms (Ajout doc, Recherche, Add user): blue paper,
// "✕" top right, title in the `en-tete` with a separator rule, and the
// buttons at the bottom. Centred at ~70 % of the width on desktop.
//
// Mobile: full-screen sheet, top bar with the title and ✕, and the actions
// pinned to the bottom as a centred pair with the primary on the left — the
// smartphone XD draws them that way on the wizard steps (S5 and S6), and
// the row stays sticky so the primary action is always one tap away.
//
// With `onSubmit`, the paper is a <form>: the `type="submit"` button in the
// actions submits, and Enter in a field does too.

type Props = {
  open: boolean;
  title: string;
  onClose: () => void;
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
  /**
   * Bottom-row buttons. On mobile they stack at full width. Without
   * `actions` there is no row: Recherche carries its buttons in its right
   * panel on desktop, and only on mobile does it use this row for its fixed bar.
   */
  actions?: ReactNode;
  /** Blocks closing (✕, Escape, click outside) while a submission is in progress. */
  busy?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  children: ReactNode;
};

const BrandDialog: FC<Props> = ({
  open,
  title,
  onClose,
  onSubmit,
  actions,
  busy = false,
  maxWidth = 'lg',
  children,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));

  const close = () => {
    if (!busy) onClose();
  };

  return (
    <Dialog
      className={BRAND_DIALOG_CLASS}
      open={open}
      onClose={close}
      maxWidth={maxWidth}
      fullWidth
      fullScreen={isMobile}
      PaperProps={
        onSubmit
          ? { component: 'form', onSubmit, noValidate: true }
          : undefined
      }
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          pr: 1.5,
        }}
      >
        {title}
        {/* XD: the modal's "✕" is orange, not white. */}
        <IconButton
          aria-label="Fermer"
          onClick={close}
          disabled={busy}
          sx={{
            color: colors.accent,
            '&.Mui-disabled': { color: colors.accent, opacity: 0.5 },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={(t) => ({
          // XD: the form breathes inside the modal, with generous space on
          // all four sides.
          px: 7,
          py: 5,
          [t.breakpoints.down('tablet')]: { p: 2 },
        })}
      >
        {children}
      </DialogContent>

      {actions && (
        <DialogActions
          sx={(t) => ({
            p: 2,
            gap: 1.5,
            justifyContent: 'flex-end',
            [t.breakpoints.down('tablet')]: {
              position: 'sticky',
              bottom: 0,
              // S5/S6: a centred pair, primary (last in the DOM) on the left.
              flexDirection: 'row-reverse',
              justifyContent: 'center',
              flexWrap: 'wrap',
              backgroundColor: colors.primary,
              borderTop: '1px solid rgba(255, 255, 255, 0.24)',
              '& > :not(:first-of-type)': { ml: 0 },
            },
          })}
        >
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default BrandDialog;
